import { MouseEvent, useCallback, useMemo, useState } from "react"

import { LabelColor } from "@/v2/graphql/public-generated-rq"
import { FormField, Input, useToast } from "@lumen-energy/design-system"
import { IconCornerDownRight } from "@tabler/icons-react"

import { LabelProps } from "./Label"
import {
  LabelGroupProps,
  LabelThemeColors,
  getLabelColorKeyFromValue,
  retrieveColorKey,
} from "./LabelGroup"
import { SelectLabelColor } from "./SelectLabelColor"

export interface EditLabelTextProps
  extends Pick<LabelGroupProps, "editLabelColor" | "editLabelName"> {
  labelPid: string
  initialName: string
  initialThemeColor: LabelColor
  orgLabels: Array<LabelProps>
}

export function EditLabelText({
  editLabelColor,
  editLabelName,
  labelPid,
  initialName,
  initialThemeColor,
  orgLabels,
}: EditLabelTextProps) {
  const toast = useToast()

  const [selectedColor, setSelectedColor] = useState<LabelThemeColors>(
    LabelThemeColors[initialThemeColor],
  )
  const [newName, setNewName] = useState<string>(initialName)

  const hasNameChanged = useMemo(() => {
    return newName !== initialName
  }, [newName, initialName])

  const isNewNameValid = useMemo(() => {
    return (
      newName &&
      !orgLabels.some(
        (label: LabelProps) => label.name === newName && label.pid !== labelPid,
      )
    )
  }, [orgLabels, newName, labelPid])

  const labelNameErrorText = useMemo(() => {
    if (!newName) {
      return "Name cannot be empty"
    } else if (!isNewNameValid) {
      return "Label already exists"
    } else {
      return undefined
    }
  }, [newName, isNewNameValid])

  const handleSelectColor = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      const colorSelection = event.currentTarget as HTMLInputElement
      const clickedColor = colorSelection.value as LabelThemeColors
      if (!clickedColor) return

      const labelColorKey = getLabelColorKeyFromValue(retrieveColorKey(clickedColor))
      if (!labelColorKey) {
        console.error("Unable to find label color key for value: ", clickedColor)
        return
      }

      const labelColorEnum = LabelColor[labelColorKey as keyof typeof LabelColor]

      if (hasNameChanged) {
        if (labelNameErrorText) {
          toast.error(labelNameErrorText)
          return
        } else {
          editLabelName(labelPid, newName)
        }
      }

      setSelectedColor(clickedColor)
      editLabelColor(labelPid, labelColorEnum)
    },
    [
      hasNameChanged,
      editLabelColor,
      labelPid,
      labelNameErrorText,
      toast,
      editLabelName,
      newName,
    ],
  )

  return (
    <div className="px-1">
      <label htmlFor={newName} className="pb-3 text-body2">
        Edit label
      </label>
      <FormField id="name" errorText={labelNameErrorText} required>
        <Input
          type="text"
          name={newName}
          defaultValue={initialName}
          variant="small"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.stopPropagation()
              editLabelName(labelPid, newName)
            }
          }}
          onChange={(event) => {
            setNewName(event.target.value)
          }}
          isError={!!labelNameErrorText}
        />
      </FormField>
      <p className="pt-4 text-body2">Change color</p>
      <SelectLabelColor
        handleSelectColor={handleSelectColor}
        selectedColor={selectedColor}
      />
      {/* TODO (Katherine) slide in "Hit Enter" text with a slight delay after user begins typing */}
      {hasNameChanged && isNewNameValid && (
        <p className="flex items-center gap-2 pt-2 text-caption">
          <IconCornerDownRight /> Hit enter to save option
        </p>
      )}
    </div>
  )
}
